import { getNoticesAPI } from '@/src/apis/rest/notice';
import Button from '@/src/components/atom/Button';
import Dialog from '@/src/components/atom/Dialog';
import Select from '@/src/components/atom/Select';
import Table from '@/src/components/atom/Table';
import SectionLabel from '@/src/components/molecule/SectionLabel';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Input from '@/src/components/atom/Input';
import { getMagazinesAPI } from '@/src/apis/rest/magazine';
import { ListResponse } from '@/src/types/common';
import NoticeDto from '@/src/models/NoticeDto';
import { MagazineDto } from '@/src/models/MagazineDto';
import {
  donationListColumn,
  entryListColumn,
  magazineListColumn,
  noticeListColumn,
} from '@/src/pages/pushAlarm/components/column';
import { getDonationsAPI } from '@/src/apis/rest/donation';
import { getEntriesAPI } from '@/src/apis/rest/entry';
import EntryDto from '@/src/models/EntryDto';
import DonationDto from '@/src/models/DonationDto';
import { PushAlarmContentCategory } from '@/src/models/PushAlarmInfoDto';
import { GridApi } from 'ag-grid-community';
import dayjs from 'dayjs';

interface PushAlarmContentModalProps {
  selectedId?: number;
  selectedCategory?: PushAlarmContentCategory;
  open: boolean;
  onOpenChange: (value: boolean) => void;
  reservationTime?: Date;
  dataPage?: number;
  dataPageSize?: number;
  onOkClick: (value: {
    id: number;
    title: string;
    category: PushAlarmContentCategory;
    dataPage: number;
    dataPageSize: number;
  }) => void;
}

function PushAlarmContentModal({
  selectedId,
  selectedCategory,
  open: openProps,
  onOpenChange: onOpenChangeProps,
  onOkClick,
  dataPage,
  dataPageSize,
  reservationTime,
}: PushAlarmContentModalProps) {
  // state
  const [page, setPage] = useState(dataPage ?? 1);
  const [pageSize, setPageSize] = useState(dataPageSize ?? 10);
  const [category, setCategory] = useState<PushAlarmContentCategory>(
    selectedCategory ?? 'notice',
  );
  const [title, setTitle] = useState('');

  const gridRef = useRef<AgGridReact>(null);
  const [isReady, setIsReady] = useState(false);

  const reservationTimeParam = dayjs(reservationTime).isValid()
    ? reservationTime?.toString()
    : new Date().toString();

  // query
  const {
    data: tableData,
    refetch: tableDataRefetch,
    isFetching,
    isLoading,
  } = useQuery<ListResponse<NoticeDto | MagazineDto | EntryDto | DonationDto>>({
    queryKey: ['connect-content', category, page, pageSize, title],
    queryFn: () => {
      switch (category) {
        case 'notice':
          return getNoticesAPI({
            page,
            pageSize,
            title,
            showedYn: true,
          });
        case 'entry':
          return getEntriesAPI({
            page,
            pageSize,
            titleLike: title,
            isExposed: true,
            startedAtTo: reservationTimeParam,
          });
        case 'donation':
          return getDonationsAPI({
            page,
            pageSize,
            titleLike: title,
            isExposed: true,
            startedAtTo: reservationTimeParam,
          });
        case 'magazine':
          return getMagazinesAPI({
            page,
            pageSize,
            title,
            isExposed: true,
            magazinePostBeginEndedAt: reservationTimeParam,
          });
        default:
          return getNoticesAPI({
            page,
            pageSize,
            title,
            showedYn: true,
          });
      }
    },
  });

  // event
  const handleCloseModal = () => {
    onOpenChangeProps(false);
  };
  const handleOkClick = () => {
    const target = gridRef.current?.api.getSelectedRows()?.[0];

    if (!target) {
      handleCloseModal();
      return;
    }
    onOkClick({
      id: target.id,
      title: target.title,
      category: category,
      dataPage: page,
      dataPageSize: pageSize,
    });
    handleCloseModal();
  };
  const handlePageChange = (page: number, pageSize: number) => {
    setPage(page);
    setPageSize(pageSize);

    setTimeout(() => {
      tableDataRefetch();
    }, 0);
  };
  const handleSubmitRequest = (categoryParam?: PushAlarmContentCategory) => {
    setPage(1);
    setPageSize(10);
    setCategory(categoryParam || category);

    setTimeout(() => {
      tableDataRefetch();
    }, 0);
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    handleSubmitRequest();
  };

  const getColumnDefs = useCallback(() => {
    switch (category) {
      case 'notice':
        return noticeListColumn();
      case 'magazine':
        return magazineListColumn();
      case 'donation':
        return donationListColumn();
      case 'entry':
        return entryListColumn();
    }
  }, [category]);

  const handleSetRowSelect = useCallback(
    (gridApi: GridApi<any>) => {
      if (selectedId && gridApi && tableData && isReady && page) {
        gridApi?.forEachNode(node => {
          if (node?.data?.id === selectedId && selectedCategory === category) {
            node.setSelected(true);
          }
        });
      }
    },
    [category, selectedCategory, selectedId, isReady, page, tableData],
  );

  useEffect(() => {
    if (gridRef?.current) {
      if (isLoading || isFetching) {
        gridRef.current?.api?.showLoadingOverlay();
      }
    }
  }, [gridRef, isLoading, isFetching]);

  // render
  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          className="flex-1"
          buttonColor="outline"
          onClick={handleCloseModal}
        >
          {BUTTON_CONST.cancel}
        </Button>
        <Button className="flex-1" onClick={handleOkClick}>
          {BUTTON_CONST.ok}
        </Button>
      </div>
    );
  };

  return (
    <Dialog
      open={openProps}
      onOpenChange={onOpenChangeProps}
      contentProps={{
        style: {
          width: 'calc(100vw - 24px)',
        },
        className: 'max-w-[967px]',
      }}
      title="연결 컨텐츠 선택"
      footer={renderFooter()}
    >
      <div className="flex flex-col gap-4">
        <form className="flex items-center gap-4" onSubmit={handleSubmit}>
          <div className="grid flex-1 grid-cols-[1fr_auto_1fr] gap-x-6 gap-y-4">
            <SectionLabel
              labelProps={{
                className: 'w-[144px] flex-shrink-0',
              }}
              className="items-center"
              label={<>연결 컨텐츠</>}
              value={
                <Select
                  rootProps={{
                    value: category,
                    onValueChange: e => {
                      setCategory(e as PushAlarmContentCategory);
                      setTitle('');
                      handleSubmitRequest(e as PushAlarmContentCategory);
                    },
                  }}
                  option={[
                    {
                      optionList: [
                        { label: '공지사항', value: 'notice' },
                        { label: '응모하기', value: 'entry' },
                        { label: '기부하기', value: 'donation' },
                        { label: '매거진', value: 'magazine' },
                      ],
                    },
                  ]}
                  allowClear={false}
                />
              }
            />
            <div className="border-r border-r-gray1" />
            <SectionLabel
              labelProps={{
                className: 'w-[144px] flex-shrink-0',
              }}
              className="items-center"
              label={<>제목</>}
              value={
                <Input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="컨텐츠 제목을 입력해주세요"
                  containerClassName="flex-1"
                  onClear={() => setTitle('')}
                />
              }
            />
          </div>
          <Button type="submit" className="flex-shrink-0 gap-2">
            {BUTTON_CONST.lookUp}
          </Button>
        </form>
        <div className="flex min-h-[525px] flex-col">
          <Table
            key={category}
            isSelectToRadio={true}
            containerClassName="flex-1 flex flex-col"
            tableClassName="flex-1"
            ref={gridRef}
            rowData={tableData?.rows || []}
            isPaginationDataMapping
            columnDefs={getColumnDefs()}
            onGridReady={() => {
              setIsReady(true);
            }}
            total={tableData?.count || 0}
            page={page}
            pageSize={pageSize}
            handlePaginationClick={handlePageChange}
            rowSelection="single"
            onComponentStateChanged={e => {
              handleSetRowSelect(e?.api);
            }}
            onBodyScrollEnd={e => {
              handleSetRowSelect(e?.api);
            }}
          />
        </div>
      </div>
    </Dialog>
  );
}

export default React.memo(PushAlarmContentModal);
